import { FileEntryFile } from "@eworkit/iris";
import { useCallback, useEffect, useReducer } from "react";
import { FileRejection } from "react-dropzone";
import { useTranslation } from "react-i18next";

import { Attachment, cloneFile, TAttachmentPurpose } from "@/data/attachments";
import { useToasts } from "@/views/components/Toast/useToasts";

import { uploadFileAws } from "../uploadFileAws";
import { FileUploadError } from "./FileUploadError";
import { FileUploadStateItem, getFileUploadInitialState, getFileUploadReducer } from "./fileUploadReducer";
import { prevalidateFile } from "./prevalidateFile";

interface SingleFileUploadOptions {
  attachment?: Attachment;
  onChange?: (value?: FileUploadStateItem<Attachment>) => void;
  attachmentType?: TAttachmentPurpose;
  parsing?: boolean;
}

export const useSingleFileUpload = ({
  attachment,
  onChange,
  attachmentType = TAttachmentPurpose.ATTACHMENT,
  parsing,
}: SingleFileUploadOptions) => {
  const initialFileUploadState = getFileUploadInitialState<Attachment>();
  const fileReducer = getFileUploadReducer<Attachment>();
  const initialItems = attachment ? [{ attachment, progress: 100 }] : [];

  const { t } = useTranslation("session");
  const { addToast } = useToasts();

  const [state, dispatch] = useReducer(fileReducer, {
    ...initialFileUploadState,
    items: initialItems,
  });

  const upload = useCallback(
    async (file: File) => {
      const prevalidationError = prevalidateFile(file);

      if (prevalidationError) {
        dispatch({
          type: "UPDATE_ITEM_BY_FILE",
          file,
          update: item => ({
            ...item,
            error: prevalidationError,
            progress: 100,
          }),
        });

        return;
      }

      try {
        const attachment = await uploadFileAws({
          file,
          type: attachmentType,
          parsing,
          onUploadProgress: progress => {
            dispatch({
              type: "UPDATE_ITEM_BY_FILE",
              file,
              update: item => ({ ...item, progress: Math.min(progress, 99.9) }),
            });
          },
        });

        if (parsing && !attachment.parsed) {
          addToast({
            type: "warning",
            text: t("ONBOARDING.PROFILE_TYPE_SELECT.PRIVATE_PERSON.UPLOAD_CV.PARSING_ERROR"),
          });
        }

        dispatch({
          type: "UPDATE_ITEM_BY_FILE",
          file,
          update: item => ({ ...item, attachment, progress: 100 }),
        });
      } catch (error) {
        dispatch({
          type: "UPDATE_ITEM_BY_FILE",
          file,
          update: item => ({
            ...item,
            error: error.payload?.code === "UNKNOWN" ? "GENERAL" : error.payload?.code ?? "GENERAL",
            progress: 100,
          }),
        });
      }
    },
    [attachmentType, parsing, addToast, t],
  );

  const addDropzoneFiles = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (state.items.length > 0) {
        dispatch({ type: "REMOVE_ITEM", item: state.items[0] });
      }
      dispatch({ type: "ADD_FILES", files: acceptedFiles });
      dispatch({ type: "ADD_FILES", files: rejectedFiles.map(rf => rf.file) });

      await Promise.all(acceptedFiles.map(file => upload(file)));

      rejectedFiles.forEach(rejectedFile => {
        const fileError = rejectedFile.errors[0];

        let validationError: FileUploadError = "GENERAL";
        switch (fileError.code) {
          case "file-invalid-type":
            validationError = "FILE_BAD_CONTENT_TYPE";
            break;
          case "file-too-large":
            validationError = "FILE_TOO_BIG";
            break;
        }

        dispatch({
          type: "UPDATE_ITEM_BY_FILE",
          file: rejectedFile.file,
          update: item => ({ ...item, error: validationError, progress: 100 }),
        });
      });
    },
    [state, upload, dispatch],
  );

  const addAttachment = useCallback((attachment: Attachment) => {
    dispatch({ type: "ADD_ATTACHMENTS", attachments: [attachment] });
  }, []);

  const addClonedAttachment = useCallback(async (attachment: Attachment) => {
    const tempFile = { name: attachment.name } as File;

    dispatch({ type: "ADD_FILES", files: [tempFile] });

    try {
      const clonedCv = await cloneFile({ systemId: attachment.systemId });

      dispatch({
        type: "UPDATE_ITEM_BY_FILE",
        file: tempFile,
        update: item => ({ ...item, attachment: clonedCv, progress: 100 }),
      });
    } catch (error) {
      dispatch({
        type: "UPDATE_ITEM_BY_FILE",
        file: tempFile,
        update: item => ({ ...item, error: error.payload?.code ?? "GENERAL", progress: 100 }),
      });
    }
  }, []);

  const deleteFileEntry = useCallback(
    (file: FileEntryFile) => {
      const item = state.items.find(i => i.file === file || i.attachment === file);
      if (item) {
        dispatch({
          type: "REMOVE_ITEM",
          item,
        });
      }
    },
    [state, dispatch],
  );

  useEffect(() => {
    onChange?.(state.items[0]);
  }, [state.items, onChange]);

  return {
    state,
    dispatch,

    addDropzoneFiles,
    addAttachment,
    addClonedAttachment,
    deleteFileEntry,
  };
};
